<template>
  <v-dialog :value="open" width="100%" max-width="1000" persistent scrollable>
    <v-card>
      <v-card-title>
        <div>
          <div class="title mb-1">{{ agentId }}</div>
          <div class="subheading secondary--text">
            Status:
            <v-icon
              small
              :color="online ? 'success' : 'error'"
              :style="{ verticalAlign: 'middle' }"
            >
              circle
            </v-icon>
            {{ online ? 'Online' : 'Offline' }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="handleCloseButtonClick">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-layout wrap align-center>
          <v-flex shrink class="default-input pa-2">
            <div class="caption">Start date</div>
            <DatepickerCustomDialog
              v-model="selectedDate.start"
              :allowed-dates="allowedDates"
              @input="handleChangeStartDate"
            >
              <v-text-field
                class="custom-datepicker-input"
                :value="toFormattedDate(selectedDate.start)"
                single-line
                outline
                hide-details
                readonly
              ></v-text-field>
            </DatepickerCustomDialog>
          </v-flex>
          <v-flex shrink class="default-input pa-2">
            <div class="caption">End date</div>
            <DatepickerCustomDialog
              v-model="selectedDate.end"
              :allowed-dates="allowedDates"
              @input="handleChangeEndDate"
            >
              <v-text-field
                class="custom-datepicker-input"
                :value="toFormattedDate(selectedDate.end)"
                single-line
                outline
                hide-details
                readonly
              ></v-text-field>
            </DatepickerCustomDialog>
          </v-flex>
          <v-spacer></v-spacer>
          <v-flex shrink class="pa-2">
            <v-btn color="primary" @click="handleExportButtonClick">
              <v-icon>logout</v-icon>
              Export
            </v-btn>
          </v-flex>
        </v-layout>
        <v-data-table
          :headers="headers"
          :items="clearDataTime && clearDataTime.length ? clearDataTime : data"
          hide-actions
          class="mt-3"
          :style="{ minHeight: '200px' }"
        >
          <template #items="props">
            <td>{{ props.item.date | DateFormat('DD/MM/YYYY') }}</td>
            <td class="py-2">
              <template v-if="props.item.checkin.length > 0">
                <div v-for="(val, index) in props.item.checkin" :key="index">
                  <span>
                    {{ val }}
                  </span>
                </div>
              </template>
              <template v-else>-</template>
            </td>
            <td class="py-2">
              <template v-if="props.item.checkout.length > 0">
                <div v-for="(val, index) in props.item.checkout" :key="index">
                  <span>
                    {{ val }}
                  </span>
                </div>
              </template>
              <template v-else>-</template>
            </td>
            <td v-if="props.item.sumDiff || props.item.sumDiff == 0">
              {{ props.item.sumDiff | timer }}
            </td>
            <td v-else>{{ getTimeDiff(props.item.checkin, props.item.checkout) | timer }}</td>
          </template>
        </v-data-table>
        <!-- 
        <v-layout class="mt-2">
          <v-pagination
            :key="timestamp"
            v-model="localPagination.page"
            :length="totalPages"
            class="ml-auto"
            @input="handlePaginationInput"
          ></v-pagination>
        </v-layout> -->

        <v-card color="grey lighten-5" class="mt-3">
          <v-card-text class="title">
            <v-layout wrap justify-space-between align-center>
              <v-flex shrink> Total: </v-flex>
              <v-flex shrink>
                {{ totalSeconds | timer }}
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import dayjs from 'dayjs'
import { DATETIME } from '@/constants/datetime'
import DatepickerCustomDialog from '@/components/elements/DatepickerCustomDialog.vue'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)

export default {
  components: {
    DatepickerCustomDialog
  },
  filters: {
    timer(value) {
      const hours = Math.floor(value / 60 / 60)
      const minutes = Math.ceil(value / 60) % 60
      return `${hours} hours ${minutes} minutes`
    }
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    agentId: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    online: {
      type: Boolean,
      default: false
    },
    search: {
      type: Object,
      default: () => ({})
    },
    items: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      data: [],
      localSearch: {
        startDate: dayjs().format(DATETIME.DATE_FORMAT),
        endDate: dayjs().format(DATETIME.DATE_FORMAT),
        ...this.search
      },
      selectedDate: {
        end: dayjs().format(DATETIME.DATE_FORMAT),
        start: dayjs().subtract(7, 'days').format(DATETIME.DATE_FORMAT)
      },
      localPagination: {
        page: 1,
        totalItems: 0,
        rowsPerPage: 10,
        sortby: 'date',
        ...this.pagination
      },
      timestamp: 0,
      headers: [
        {
          text: 'Date',
          value: 'date',
          sortable: false
        },
        {
          text: 'Check-in time',
          value: 'checkInTime',
          sortable: false
        },
        {
          text: 'Check-out time',
          value: 'checkOutTime',
          sortable: false
        },
        {
          text: 'Duration',
          sortable: false
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    }),
    totalPages() {
      const { totalItems, rowsPerPage } = this.localPagination
      return Math.ceil(totalItems / rowsPerPage)
    },
    clearDataTime() {
      const output = []
      for (let i = 0; i < this.data.length; i++) {
        const dayTemp = this.data[i]
        const checkInList = dayTemp.checkin
        const checkOutList = dayTemp.checkout
        const checkInOutput = []
        const checkOutOutput = []
        const diffOutput = []
        for (let j = 0; j < checkInList.length; j++) {
          checkInOutput.push(checkInList[j])
          let checkOutData = null
          for (let k = 0; k < checkOutList.length; k++) {
            if (!checkOutData) {
              checkOutData = checkOutList[0]
            } else {
              if (checkInList[j + 1]) {
                let checkInDayNext = dayjs(`1999-01-01 ${checkInList[j + 1]}`)
                let selectDay = dayjs(`1999-01-01 ${checkOutData}`)
                let candidateDay = dayjs(`1999-01-01 ${checkOutList[k]}`)
                if (checkInDayNext.diff(candidateDay) > 0) {
                  if (candidateDay.diff(selectDay) > 0) {
                    checkOutData = checkOutList[k]
                  }
                }
              } else {
                checkOutData = checkOutList[k]
              }
            }
          }
          if (checkOutData) {
            checkOutOutput.push(checkOutData)
            const startTime = dayjs(`1999-01-01 ${checkInList[j]}`)
            const endTime = dayjs(`1999-01-01 ${checkOutData}`)
            const diffTime = endTime.diff(startTime)
            if (diffTime > 0) {
              diffOutput.push(diffTime / 1000)
            } else {
              diffOutput.push(0)
            }
          }
        }
        const sumSec = diffOutput.reduce(
          (accumulator, currentValue) => accumulator + currentValue,
          0
        )
        if (checkInList.length) {
          output.push({
            date: dayTemp.date,
            checkin: checkInList,
            checkout: checkOutOutput,
            diff: diffOutput,
            sumDiff: sumSec
          })
        }
      }
      return output
    },
    totalSeconds() {
      try {
        if (this.clearDataTime && this.clearDataTime.length) {
          const total = this.clearDataTime.reduce((acc, cur) => {
            return acc + cur.sumDiff
          }, 0)
          return total
        } else {
          const total = this.data.reduce((acc, cur) => {
            return acc + this.getTimeDiff(cur.checkin, cur.checkout)
          }, 0)
          return total
        }
      } catch (error) {
        console.error(error)
        return 0
      }
    }
  },
  watch: {
    open(value) {
      if (!value) return
      if (this.agentId) {
        this.handleFetchData()
      }
      // this.timestamp = Date.now()
    },
    search(value) {
      this.localSearch = {
        startDate: dayjs().format(DATETIME.DATE_FORMAT),
        endDate: dayjs().format(DATETIME.DATE_FORMAT),
        ...value
      }
    },
    pagination(value) {
      this.localPagination = {
        page: 1,
        totalItems: 0,
        rowsPerPage: 10,
        ...value
      }
    }
    // selectedDate: {
    //   handler() {
    //     this.handleChangeDate()
    //   },
    //   deep: true
    // }
  },
  methods: {
    async handleFetchData() {
      const vm = this
      try {
        const data = {
          pageid: vm.selectedBot.pageid,
          startTime: dayjs(vm.selectedDate.start).format(DATETIME.DATE_FORMAT),
          endTime: dayjs(vm.selectedDate.end).format(DATETIME.DATE_FORMAT),
          adminId: vm.agentId
        }

        vm.loading = true
        api.commonNewTicketCheckIncheckOutStatByAdminId(data, (error, response) => {
          vm.loading = false
          if (error) {
            console.error(error)
            return
          }

          const data = []

          if (response.responsecode === 200 && response.data && response.data.length > 0) {
            const responseData = response.data[0]

            Object.keys(responseData).forEach((key) => {
              data.push({
                date: key,
                checkin: responseData[key].checkIn,
                checkout: responseData[key].checkOut
              })
            })
          }
          vm.data = data
        })
        vm.loading = false
      } catch (error) {
        vm.loading = false
        console.error(error)
      }
    },
    toFormattedDate(date) {
      return dayjs(date).format(DATETIME.DATE_FORMAT_DISPLAY)
    },
    getTimeDiff(group_to, group_from) {
      const earliest_time = group_to
        ? group_to.reduce((a, b) => {
            return a < b ? a : b
          })
        : '00:00'

      const lasted_time =
        group_from && group_from.length
          ? group_from.reduce((a, b) => {
              return a > b ? a : b
            })
          : '00:00'
      const result = dayjs(`${lasted_time || '00:00'}:00`, 'HH:mm:ss').diff(
        dayjs(`${earliest_time || '00:00'}:00`, 'HH:mm:ss'),
        'seconds'
      )

      return result
    },
    handleCloseButtonClick() {
      this.$emit('update:open', false)
    },
    handleStartDateInput() {
      this.$emit('update:search', { ...this.localSearch })
    },
    handleEndDateInput() {
      this.$emit('update:search', { ...this.localSearch })
    },
    handleExportButtonClick() {
      this.$emit(
        'export',
        this.clearDataTime && this.clearDataTime.length ? this.clearDataTime : this.data,
        this.selectedDate,
        this.agentId
      )
    },
    handlePaginationInput() {
      this.$emit('update:pagination', { ...this.localPagination })
    },
    handleChangeStartDate() {
      let dateFrom = this.selectedDate.start
      let dateTo = this.selectedDate.end
      if (this.selectedDate.start && this.selectedDate.end) {
        if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
          this.selectedDate.end = dateFrom
        }
        this.handleFetchData()
      }
    },
    handleChangeEndDate() {
      let dateFrom = this.selectedDate.start
      let dateTo = this.selectedDate.end
      if (this.selectedDate.start && this.selectedDate.end) {
        if (dayjs(dateTo).isBefore(dayjs(dateFrom))) {
          this.selectedDate.start = dateTo
        }
        this.handleFetchData()
      }
    },
    allowedDates(val) {
      return dayjs().diff(val, 'days') > 0
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/ticket3/agent-management/agents/WorkTime/AgentDialog.vue